import { BackgroundProps, BoxProps, Badge, Flex } from '@chakra-ui/react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import {
  utils as commonUtils,
  finleyAssert,
  typeguards as commonTypeguards,
} from '@finley-technologies/common';

import { Maybe } from 'types/dashboard-core-gqlTypes.generated.d';

import { useCreditFacilityContext } from '../hooks/CreditFacilityProvider';
import { useGetCreditFacilitiesLazyQueryGQLType } from '../requests/requests.gql.generated';
import { Select } from 'packages/common-frontend/components/Select';
import { useQueryParams } from 'packages/common-frontend/hooks/useQueryParams';

export interface CreditFacilitySelectProps {
  isDisabled?: boolean;
  creditFacilitySlugFilter?: Maybe<(creditFacilitySlug: string) => boolean>;
  creditFacilitySlugFilterFailMessage?: Maybe<string>;
  organizationId: Maybe<string>;
  hideOnSingleFacility?: boolean;
  width?: BoxProps['width'];
}

export interface CreditFacilitySelectWithFloatingLabelProps
  extends CreditFacilitySelectProps {
  displayInputFloatingLabel: boolean;
  inputFloatingLabelBackgroundColor: BackgroundProps['bgColor'];
}

export interface CreditFacilitySelectWithQueryParamsProps
  extends CreditFacilitySelectWithFloatingLabelProps {
  onSelectRedirectLink: (creditFacilityId: string) => string;
}

export const CreditFacilitySelect: React.FC<
  CreditFacilitySelectProps | CreditFacilitySelectWithFloatingLabelProps
> = withLDConsumer()((props) => {
  const {
    flags,
    isDisabled,
    creditFacilitySlugFilter,
    creditFacilitySlugFilterFailMessage,
    organizationId,
    hideOnSingleFacility,
    width,
  } = props;
  const creditFacilitySlugsToHide = flags?.creditFacilitySlugsToHide ?? [];

  const { creditFacilityId, setCreditFacilityId } = useCreditFacilityContext();

  const [
    getCreditFacilities,
    { data, loading },
  ] = useGetCreditFacilitiesLazyQueryGQLType({
    onCompleted: (cfData) => {
      // This block only executes from the function call in useEffect after component is rendered.
      let creditFacilities = cfData?.creditFacilities || [];
      creditFacilities = creditFacilities.filter(
        ({ slug }) => !creditFacilitySlugsToHide.includes(slug),
      );

      // When facility filter function is available, narrow down list of facilities
      if (creditFacilitySlugFilter) {
        creditFacilities = _.filter(creditFacilities, (creditFacility) =>
          creditFacilitySlugFilter(creditFacility.slug),
        );
      }

      // Find facility matching available facility after filtering if applicable, or select the first available facility
      const selectedCreditFacility =
        _.find(
          creditFacilities,
          (creditFacility) => creditFacility.id === creditFacilityId,
        ) || creditFacilities[0];

      // Set credit facility if either matches:
      // - no credit facility is available from context
      // - selected facility does not match filtered list
      if (!creditFacilityId || selectedCreditFacility.id !== creditFacilityId) {
        setCreditFacilityId(selectedCreditFacility.id);
      }
    },
  });

  useEffect(() => {
    if (organizationId) {
      getCreditFacilities({ variables: { organizationId } });
    }
  }, [organizationId, getCreditFacilities]);

  if (loading || !data || !data.creditFacilities) {
    return null;
  }

  /**
   * Logic below is reused in getCreditFacilities onCompleted callback
   * Leaving this here instead of moving to a useState variable to
   * prevent issues with initial empty render before useEffect that
   * result in the component flickering with the warning message
   * creditFacilitySlugFilterFailMessage being displayed
   */
  let enabledCreditFacilities = data.creditFacilities;
  enabledCreditFacilities = enabledCreditFacilities.filter(
    ({ slug }) => !creditFacilitySlugsToHide.includes(slug),
  );
  if (creditFacilitySlugFilter) {
    enabledCreditFacilities = _.filter(
      enabledCreditFacilities,
      (creditFacility) => {
        const { slug: creditFacilitySlug } = creditFacility;
        return creditFacilitySlugFilter(creditFacilitySlug);
      },
    );
  }

  if (hideOnSingleFacility && enabledCreditFacilities.length === 1) {
    return null;
  }

  return (
    <Flex height="100%" alignItems="center">
      {enabledCreditFacilities.length > 0 ? (
        <Select
          value={creditFacilityId}
          width={width}
          options={_.map(
            enabledCreditFacilities,
            ({ id, slug: creditFacilitySlug }) => {
              finleyAssert(
                commonTypeguards.isCreditFacilitySlug(creditFacilitySlug),
                'Expected this to be a creditFacilitySlug',
              );
              return {
                value: id,
                label: commonUtils.creditFacilitySlugToName(creditFacilitySlug),
              };
            },
          )}
          inputFloatingLabel={
            'displayInputFloatingLabel' in props &&
            // eslint-disable-next-line react/destructuring-assignment
            props.displayInputFloatingLabel
              ? 'Credit Facility'
              : ''
          }
          inputFloatingLabelBackgroundColor={
            'inputFloatingLabelBackgroundColor' in props
              ? // eslint-disable-next-line react/destructuring-assignment
                props.inputFloatingLabelBackgroundColor
              : undefined
          }
          onChange={(value) => {
            if (value) setCreditFacilityId(value);
          }}
          isDisabled={isDisabled}
        />
      ) : (
        <Badge colorScheme="red">
          {creditFacilitySlugFilter && creditFacilitySlugFilterFailMessage
            ? creditFacilitySlugFilterFailMessage
            : 'None activated'}
        </Badge>
      )}
    </Flex>
  );
});

export const creditFacilityIdQueryParam = 'creditFacility';
export const CreditFacilitySelectWithQueryParams: React.FC<CreditFacilitySelectWithQueryParamsProps> = withLDConsumer()(
  (props) => {
    const {
      flags,
      isDisabled,
      creditFacilitySlugFilter,
      creditFacilitySlugFilterFailMessage,
      organizationId,
      hideOnSingleFacility,
      width,
      onSelectRedirectLink,
    } = props;
    const creditFacilitySlugsToHide = flags?.creditFacilitySlugsToHide ?? [];

    const history = useHistory();
    const query = useQueryParams();
    const queryParamCreditFacilityId = query.get(creditFacilityIdQueryParam);

    const [creditFacilityId, setCreditFacilityId] = useState(
      queryParamCreditFacilityId,
    );

    const [
      getCreditFacilities,
      { data, loading },
    ] = useGetCreditFacilitiesLazyQueryGQLType({
      onCompleted: (cfData) => {
        // This block only executes from the function call in useEffect after component is rendered.
        let creditFacilities = cfData?.creditFacilities || [];
        creditFacilities = creditFacilities.filter(
          ({ slug }) => !creditFacilitySlugsToHide.includes(slug),
        );

        // When facility filter function is available, narrow down list of facilities
        if (creditFacilitySlugFilter) {
          creditFacilities = _.filter(creditFacilities, (creditFacility) =>
            creditFacilitySlugFilter(creditFacility.slug),
          );
        }

        // Find facility matching available facility after filtering if applicable, or select the first available facility
        const selectedCreditFacility =
          _.find(
            creditFacilities,
            (creditFacility) => creditFacility.id === creditFacilityId,
          ) || creditFacilities[0];

        // Set credit facility if either matches:
        // - no credit facility is available from context
        // - selected facility does not match filtered list
        if (
          !creditFacilityId ||
          selectedCreditFacility.id !== creditFacilityId
        ) {
          setCreditFacilityId(selectedCreditFacility.id);
        }
      },
    });

    useEffect(() => {
      if (organizationId) {
        getCreditFacilities({ variables: { organizationId } });
      }
    }, [organizationId, getCreditFacilities]);

    if (loading || !data || !data.creditFacilities) {
      return null;
    }

    /**
     * Logic below is reused in getCreditFacilities onCompleted callback
     * Leaving this here instead of moving to a useState variable to
     * prevent issues with initial empty render before useEffect that
     * result in the component flickering with the warning message
     * creditFacilitySlugFilterFailMessage being displayed
     */
    let enabledCreditFacilities = data.creditFacilities;
    enabledCreditFacilities = enabledCreditFacilities.filter(
      ({ slug }) => !creditFacilitySlugsToHide.includes(slug),
    );
    if (creditFacilitySlugFilter) {
      enabledCreditFacilities = _.filter(
        enabledCreditFacilities,
        (creditFacility) => {
          const { slug: creditFacilitySlug } = creditFacility;
          return creditFacilitySlugFilter(creditFacilitySlug);
        },
      );
    }

    if (hideOnSingleFacility && enabledCreditFacilities.length === 1) {
      return null;
    }

    const onCreditFacilitySelect = (selectedCreditFacilityId: string) => {
      setCreditFacilityId(selectedCreditFacilityId);
      const redirectLink = onSelectRedirectLink(selectedCreditFacilityId);
      history.push(redirectLink);
    };

    return (
      <Flex height="100%" alignItems="center">
        {enabledCreditFacilities.length > 0 ? (
          <Select
            value={creditFacilityId || enabledCreditFacilities[0].id}
            width={width}
            options={_.map(
              enabledCreditFacilities,
              ({ id, slug: creditFacilitySlug }) => {
                finleyAssert(
                  commonTypeguards.isCreditFacilitySlug(creditFacilitySlug),
                  'Expected this to be a creditFacilitySlug',
                );
                return {
                  value: id,
                  label: commonUtils.creditFacilitySlugToName(
                    creditFacilitySlug,
                  ),
                };
              },
            )}
            inputFloatingLabel={
              'displayInputFloatingLabel' in props &&
              // eslint-disable-next-line react/destructuring-assignment
              props.displayInputFloatingLabel
                ? 'Credit Facility'
                : ''
            }
            inputFloatingLabelBackgroundColor={
              'inputFloatingLabelBackgroundColor' in props
                ? // eslint-disable-next-line react/destructuring-assignment
                  props.inputFloatingLabelBackgroundColor
                : undefined
            }
            onChange={(value) => {
              if (value) onCreditFacilitySelect(value);
            }}
            isDisabled={isDisabled}
          />
        ) : (
          <Badge colorScheme="red">
            {creditFacilitySlugFilter && creditFacilitySlugFilterFailMessage
              ? creditFacilitySlugFilterFailMessage
              : 'None activated'}
          </Badge>
        )}
      </Flex>
    );
  },
);

export const CreditFacilitySelectWithUrlParams: React.FC<CreditFacilitySelectWithQueryParamsProps> = withLDConsumer()(
  (props) => {
    const {
      flags,
      isDisabled,
      creditFacilitySlugFilter,
      creditFacilitySlugFilterFailMessage,
      organizationId,
      hideOnSingleFacility,
      width,
      onSelectRedirectLink,
    } = props;
    const creditFacilitySlugsToHide = flags?.creditFacilitySlugsToHide ?? [];

    const history = useHistory();
    const { creditFacilityId: creditFacilityIdParam } = useParams<{
      creditFacilityId: string;
    }>();

    const [creditFacilityId, setCreditFacilityId] = useState(
      creditFacilityIdParam,
    );

    const [
      getCreditFacilities,
      { data, loading },
    ] = useGetCreditFacilitiesLazyQueryGQLType({
      onCompleted: (cfData) => {
        // This block only executes from the function call in useEffect after component is rendered.
        let creditFacilities = cfData?.creditFacilities || [];
        creditFacilities = creditFacilities.filter(
          ({ slug }) => !creditFacilitySlugsToHide.includes(slug),
        );

        // When facility filter function is available, narrow down list of facilities
        if (creditFacilitySlugFilter) {
          creditFacilities = _.filter(creditFacilities, (creditFacility) =>
            creditFacilitySlugFilter(creditFacility.slug),
          );
        }

        // Find facility matching available facility after filtering if applicable, or select the first available facility
        const selectedCreditFacility =
          _.find(
            creditFacilities,
            (creditFacility) => creditFacility.id === creditFacilityId,
          ) || creditFacilities[0];

        // Set credit facility if either matches:
        // - no credit facility is available from context
        // - selected facility does not match filtered list
        if (
          !creditFacilityId ||
          selectedCreditFacility.id !== creditFacilityId
        ) {
          setCreditFacilityId(selectedCreditFacility.id);
        }
      },
    });

    useEffect(() => {
      if (organizationId) {
        getCreditFacilities({ variables: { organizationId } });
      }
    }, [organizationId, getCreditFacilities]);

    if (loading || !data || !data.creditFacilities) {
      return null;
    }

    /**
     * Logic below is reused in getCreditFacilities onCompleted callback
     * Leaving this here instead of moving to a useState variable to
     * prevent issues with initial empty render before useEffect that
     * result in the component flickering with the warning message
     * creditFacilitySlugFilterFailMessage being displayed
     */
    let enabledCreditFacilities = data.creditFacilities;
    enabledCreditFacilities = enabledCreditFacilities.filter(
      ({ slug }) => !creditFacilitySlugsToHide.includes(slug),
    );
    if (creditFacilitySlugFilter) {
      enabledCreditFacilities = _.filter(
        enabledCreditFacilities,
        (creditFacility) => {
          const { slug: creditFacilitySlug } = creditFacility;
          return creditFacilitySlugFilter(creditFacilitySlug);
        },
      );
    }

    if (hideOnSingleFacility && enabledCreditFacilities.length === 1) {
      return null;
    }

    const onCreditFacilitySelect = (selectedCreditFacilityId: string) => {
      setCreditFacilityId(selectedCreditFacilityId);
      const redirectLink = onSelectRedirectLink(selectedCreditFacilityId);
      history.push(redirectLink);
    };

    return (
      <Flex height="100%" alignItems="center">
        {enabledCreditFacilities.length > 0 ? (
          <Select
            value={creditFacilityId || enabledCreditFacilities[0].id}
            width={width}
            options={_.map(
              enabledCreditFacilities,
              ({ id, slug: creditFacilitySlug }) => {
                finleyAssert(
                  commonTypeguards.isCreditFacilitySlug(creditFacilitySlug),
                  'Expected this to be a creditFacilitySlug',
                );
                return {
                  value: id,
                  label: commonUtils.creditFacilitySlugToName(
                    creditFacilitySlug,
                  ),
                };
              },
            )}
            inputFloatingLabel={
              'displayInputFloatingLabel' in props &&
              // eslint-disable-next-line react/destructuring-assignment
              props.displayInputFloatingLabel
                ? 'Credit Facility'
                : ''
            }
            inputFloatingLabelBackgroundColor={
              'inputFloatingLabelBackgroundColor' in props
                ? // eslint-disable-next-line react/destructuring-assignment
                  props.inputFloatingLabelBackgroundColor
                : undefined
            }
            onChange={(value) => {
              if (value) onCreditFacilitySelect(value);
            }}
            isDisabled={isDisabled}
          />
        ) : (
          <Badge colorScheme="red">
            {creditFacilitySlugFilter && creditFacilitySlugFilterFailMessage
              ? creditFacilitySlugFilterFailMessage
              : 'None activated'}
          </Badge>
        )}
      </Flex>
    );
  },
);
